import { UserSearch, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CandidateCheckMatching, CandidateCheckStatus } from '../dealroom/types.js';
import { useTRPC } from '../trpc.js';
import { ConfirmModal } from '../ui/ConfirmModal.js';
import { formatCount } from '../ui/format.js';
import { Switch } from '../ui/Switch.js';
import { useToast } from '../ui/Toast.js';

/** How often the page asks how a running check is getting on. */
const POLL_MS = 3_000;

/** Which candidates a check is about, as the api takes it. */
export type CheckSelection =
  | { kind: 'ids'; candidateIds: readonly string[] }
  /** Every pending candidate the list's filters keep: the api resolves them. */
  | { kind: 'matching'; matching: CandidateCheckMatching; count: number };

function selectionInput(selection: CheckSelection, force: boolean) {
  return selection.kind === 'ids'
    ? { candidateIds: [...selection.candidateIds], force }
    : { matching: selection.matching, force };
}

function isLive(status: CandidateCheckStatus | undefined): boolean {
  const run = status?.run;
  return run !== undefined && run !== null && (run.status === 'queued' || run.status === 'running');
}

const NO_IDS: ReadonlySet<string> = new Set();

/**
 * The latest check, as every view of it reads it: polled while it is queued
 * or running, so closing and reopening the Suggest drawer, or moving between
 * the drawer and the Discovered tab, picks the same run up where it is.
 * `checking` is the candidates it is still to answer, for their rows.
 */
export function useCandidateCheck(): {
  status: CandidateCheckStatus | undefined;
  live: boolean;
  checking: ReadonlySet<string>;
} {
  const trpc = useTRPC();
  const status = useQuery({
    ...trpc.candidates.affinityCheckStatus.queryOptions(),
    refetchInterval: (query) => (isLive(query.state.data) ? POLL_MS : false),
  });
  // Read so each poll re-renders: "Starting now" turns into the tick's wait
  // without the run itself changing.
  void status.dataUpdatedAt;
  const ids = status.data?.run?.candidateIds;
  const checking = useMemo(() => (ids === undefined ? NO_IDS : new Set(ids)), [ids]);
  return { status: status.data, live: isLive(status.data), checking };
}

/**
 * The toolbar's "Check Affinity (N)": the projected calls first, in a confirm,
 * then the check queued; while it runs, a status line with its progress, and
 * the list read again as it finishes so the "Know anyone?" column fills in.
 */
export function AffinityCheckButton(props: {
  selection: CheckSelection | null;
  onStarted: () => void;
  /** Told when a running check ends, beyond the Discover list it already refreshes. */
  onFinished?: () => void;
}): React.JSX.Element | null {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const [force, setForce] = useState(false);

  const { status, live } = useCandidateCheck();
  const otherRunActive = status?.otherRunActive === true;

  // A check that just finished changes the rows on screen.
  const wasLive = useRef(live);
  const { onFinished } = props;
  useEffect(() => {
    if (wasLive.current && !live) {
      void queryClient.invalidateQueries({ queryKey: trpc.candidates.list.queryKey() });
      onFinished?.();
    }
    wasLive.current = live;
  }, [live, queryClient, trpc, onFinished]);

  const plan = useQuery({
    ...trpc.candidates.affinityCheckPlan.queryOptions(
      props.selection === null
        ? { candidateIds: [], force }
        : selectionInput(props.selection, force),
    ),
    enabled: confirming && props.selection !== null,
  });

  const start = useMutation(
    trpc.candidates.checkAffinity.mutationOptions({
      onSuccess: (result) => {
        setConfirming(false);
        setForce(false);
        props.onStarted();
        toast(
          `Queued: checking ${fundCount(result.plan.toCheck)} in Affinity (about ${formatCount(result.plan.projectedCalls)} calls). Progress shows beside the button.`,
          { tone: 'info' },
        );
        void queryClient.invalidateQueries({
          queryKey: trpc.candidates.affinityCheckStatus.queryKey(),
        });
      },
      onError: (error) => {
        setConfirming(false);
        setForce(false);
        toast(`The check did not start. ${error.message}`);
        // Whatever refused it (another run, most likely) shows in the status.
        void queryClient.invalidateQueries({
          queryKey: trpc.candidates.affinityCheckStatus.queryKey(),
        });
      },
    }),
  );

  const count =
    props.selection === null
      ? 0
      : props.selection.kind === 'ids'
        ? props.selection.candidateIds.length
        : props.selection.count;

  return (
    <>
      {props.selection !== null && count > 0 && (
        <button
          className="button"
          type="button"
          disabled={live || start.isPending}
          title={live ? 'A check is already queued or running.' : undefined}
          onClick={() => setConfirming(true)}
        >
          <UserSearch aria-hidden="true" />
          Check Affinity ({formatCount(count)})
        </button>
      )}
      <CheckProgress status={status} />
      {confirming && props.selection !== null && (
        <ConfirmModal
          title={
            plan.data === undefined
              ? 'Check these in Affinity?'
              : `Check ${formatCount(plan.data.toCheck)} Discovered fund${plan.data.toCheck === 1 ? '' : 's'} in Affinity?`
          }
          body={
            <>
              {otherRunActive && (
                <>
                  {BUSY_LINE}
                  <br />
                </>
              )}
              {plan.data === undefined ? (
                plan.isError ? (
                  `Could not work out what it would cost. ${plan.error.message}`
                ) : (
                  'Working out what it will cost…'
                )
              ) : (
                <>
                  About {formatCount(plan.data.projectedCalls)} Affinity call
                  {plan.data.projectedCalls === 1 ? '' : 's'}: one or two a fund (its website, then
                  its name).
                  {plan.data.skippedRecent > 0 &&
                    ` ${formatCount(plan.data.skippedRecent)} checked in the last 90 days ${force ? 'will be asked again' : 'are skipped'}.`}
                  {plan.data.projectedCalls > plan.data.ceiling &&
                    ` That is over the ${formatCount(plan.data.ceiling)} one run may spend: select fewer.`}
                </>
              )}
              <br />
              <Switch checked={force} onChange={setForce}>
                Ask again about ones checked recently
              </Switch>
            </>
          }
          confirmLabel={
            start.isPending
              ? 'Starting…'
              : plan.data === undefined
                ? 'Check'
                : `Check (~${formatCount(plan.data.projectedCalls)} calls)`
          }
          onConfirm={() => {
            if (
              props.selection === null ||
              plan.data === undefined ||
              plan.data.toCheck === 0 ||
              start.isPending
            )
              return;
            start.mutate(selectionInput(props.selection, force));
          }}
          onCancel={() => {
            setConfirming(false);
            setForce(false);
          }}
        />
      )}
    </>
  );
}

const BUSY_LINE =
  'Another Affinity sync is running, so a check would be refused. Try again in a few minutes.';

/** How long a finished check's summary stays beside the button. */
const SUMMARY_FOR_MS = 24 * 60 * 60 * 1000;
/** A woken worker boots within a minute or so; past this, say it waits for the tick. */
const PROMPT_START_MS = 2 * 60 * 1000;

function fundCount(count: number): string {
  return `${formatCount(count)} fund${count === 1 ? '' : 's'}`;
}

type CheckRun = NonNullable<CandidateCheckStatus['run']>;

/** "2 known, 1 not in Affinity": the answers, most useful first, none that is zero. */
function answersText(counts: CheckRun['counts']): string {
  const parts: readonly (readonly [number, string])[] = [
    [counts.known, 'known'],
    [counts.not_known, 'not known'],
    [counts.no_match, 'not in Affinity'],
    [counts.ambiguous, 'unclear'],
    [counts.error, 'failed'],
  ];
  const said = parts.flatMap(([count, label]) =>
    count === 0 ? [] : [`${formatCount(count)} ${label}`],
  );
  return said.length === 0 ? 'nothing needed asking' : said.join(', ');
}

/** Why a check stopped, in a sentence a person can act on. */
function failureText(error: string | null): string {
  if (error !== null && /call ceiling/i.test(error))
    return 'it reached the most Affinity calls one check may make.';
  if (error !== null && /interrupted/i.test(error)) return 'the worker stopped part way.';
  return 'something went wrong on the way (hover for the detail).';
}

/**
 * Where the latest check is, in words: queued (and when it will start),
 * "Checking Affinity… 3 of 12", then what it found or why it stopped, which
 * stays for a day unless dismissed.
 */
function CheckProgress(props: {
  status: CandidateCheckStatus | undefined;
}): React.JSX.Element | null {
  const [dismissed, setDismissed] = useState<string | null>(null);
  const run = props.status?.run;
  if (run === undefined || run === null || run.id === dismissed) return null;
  const finishedLongAgo =
    run.finishedAt !== null && Date.now() - new Date(run.finishedAt).getTime() > SUMMARY_FOR_MS;
  const dismiss = (
    <button
      className="icon-button"
      type="button"
      aria-label="Dismiss"
      title="Dismiss"
      onClick={() => setDismissed(run.id)}
    >
      <X aria-hidden="true" />
    </button>
  );
  switch (run.status) {
    case 'queued': {
      const waited = Date.now() - new Date(run.createdAt).getTime();
      const when =
        props.status?.startsPromptly === true && waited < PROMPT_START_MS
          ? 'Starting now.'
          : 'It usually starts within 10 minutes.';
      return (
        <span className="subtle" role="status">
          Queued: checking {fundCount(run.total)} in Affinity. {when}
        </span>
      );
    }
    case 'running':
      return (
        <span className="subtle" role="status">
          Checking Affinity… {formatCount(run.done)} of {formatCount(run.total)} ·{' '}
          {formatCount(run.callsMade)} calls
        </span>
      );
    case 'done':
      if (finishedLongAgo) return null;
      return (
        <span className="subtle" role="status">
          Affinity check done: {answersText(run.counts)}. {dismiss}
        </span>
      );
    case 'failed':
      if (finishedLongAgo) return null;
      return (
        <span className="subtle" role="status" title={run.error ?? undefined}>
          Affinity check stopped after {formatCount(run.done)} of {formatCount(run.total)}
          {run.done > 0 ? ` (${answersText(run.counts)}, kept)` : ''}: {failureText(run.error)}{' '}
          Select the rest and check again. {dismiss}
        </span>
      );
    case 'cancelled':
      return null;
    default: {
      const unreachable: never = run.status;
      return <span className="subtle">{String(unreachable)}</span>;
    }
  }
}
